import { FunctionComponent, h } from 'preact';
import { useState, useCallback, useMemo } from 'preact/hooks';
import classNames from 'classnames';
import { useTranslation } from '../../hooks/use-translation';
import { useStore } from '../../helpers/use-store';
import { map as _map, get as _get } from 'lodash';
import { observer } from 'mobx-react';
import SelectPickup from './select-pickup/select-pickup';
import { AddressProps } from './select-pickup/address/address';
import { ModalState } from '../action-modal/action-modal';
import ConfirmPickupLocationModal from './confirm-pickup-location-modal/confirm-pickup-location-modal';

interface Props {
	onSelectedChanged: (selectedIndex: number) => void;
	onSelectVisibilityChanged: (isSelectVisible: boolean) => void;
}

const milesToKM = (miles: number) => {
	return Number((miles * 1.6).toFixed(2));
};

const SelectPickupLocation: FunctionComponent<Props> = observer(({ onSelectedChanged, onSelectVisibilityChanged }) => {
	const { t } = useTranslation();
	const [selectModalOpen, setSelectModalOpen] = useState(false);
	const [confirmSelectModalOpen, setConfirmSelectModalOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [selectedPickupLocation, setSelectedPickupLocation] = useState(0);
	const [modalState, setModalState] = useState(ModalState.Idle);
	const { sharedLocationStore } = useStore();
	const { redirectDeliveryConfiguration } = sharedLocationStore.sharedLocationConfig;

	const toggleSelectPickupModal = useCallback(() => {
		onSelectVisibilityChanged(!selectModalOpen);
		setSelectModalOpen(isOpen => !isOpen);
	}, [selectModalOpen]);

	const addresses = useMemo((): AddressProps[] => {
		return _map(_get(redirectDeliveryConfiguration, 'teams'), team => {
			return {
				address: team.address,
				distance: milesToKM(team.distance),
				description: team.name,
				translations: { distanceUnit: 'km' }
			};
		});
	}, [redirectDeliveryConfiguration]);

	const onSelectedAddressChanged = useCallback((selectedIndex: number) => {
		onSelectedChanged(selectedIndex);
		setSelectedPickupLocation(selectedIndex);
	}, []);

	const redirectOrder = useCallback(async () => {
		try {
			setLoading(true);
			const selectedTeam = redirectDeliveryConfiguration.teams[selectedPickupLocation];
			await sharedLocationStore.redirectOrder(selectedTeam.id);
			setModalState(ModalState.Success);
		} catch (error) {
			setModalState(ModalState.Fail);
		} finally {
			setLoading(false);
		}
	}, [selectedPickupLocation, redirectDeliveryConfiguration]);

	const onSubmitPickupLocation = useCallback(() => {
		setModalState(ModalState.Idle);
		setConfirmSelectModalOpen(true);
	}, [confirmSelectModalOpen]);

	return (
		<span className="select-pickup-location">
			<button
				className={classNames('pickup-location-button filled-button', {
					'selected-pickup-location-button': selectModalOpen
				})}
				onClick={toggleSelectPickupModal}
			>
				{t('REDIRECT_PICKUP.PICKUP_OPTIONS')}
			</button>
			{selectModalOpen && (
				<SelectPickup
					addresses={addresses}
					onClickAddress={onSelectedAddressChanged}
					activeIndex={selectedPickupLocation}
					onCancel={toggleSelectPickupModal}
					onSubmit={onSubmitPickupLocation}
					translations={{
						header: t('REDIRECT_PICKUP.SELECT_PICKUP_LOCATION'),
						submitButtonText: t('REDIRECT_PICKUP.SET_AS_PICKUP_LOCATION')
					}}
				/>
			)}
			{!!addresses.length && (
				<ConfirmPickupLocationModal
					loading={loading}
					isOpen={confirmSelectModalOpen}
					state={modalState}
					onOk={redirectOrder}
					onCancel={() => setConfirmSelectModalOpen(false)}
					address={addresses[selectedPickupLocation]}
				/>
			)}
		</span>
	);
});

export default SelectPickupLocation;
